import * as Api from '@ViewModels';
import { useCallback } from 'react';
import { AutomationEditorStep } from '../../../../models/Automations';
import { useAutomationTemplateDuplicateStepMutation } from '../../../../queries/Automation/mutations/steps/useAutomationTemplateDuplicateStepMutation';
import { useAutomationTemplateRemoveStepMutation } from '../../../../queries/Automation/mutations/steps/useAutomationTemplateRemoveStepMutation';
import { useUpdateAutomationStepMutation } from '../../../../queries/Automation/mutations/steps/useUpdateAutomationStepMutation';

export interface UseStepActionsParams {
	automationTemplateId: string;
	steps: AutomationEditorStep[];
	automationTemplateTrigger?: Api.IAutomationTrigger;
	impersonationContext?: Api.IImpersonationContext;
	onStepsChanged: (steps: AutomationEditorStep[]) => void;
	createEmptyStep?: () => Promise<AutomationEditorStep[]>;
}

export interface UseStepActionsResult {
	copyStep: (step: AutomationEditorStep, beforeStepId?: string) => Promise<void>;
	deleteStep: (step: AutomationEditorStep) => Promise<void>;
	moveStepUp: (step: AutomationEditorStep) => void;
	moveStepDown: (step: AutomationEditorStep) => void;
	isBusy: boolean;
}

/**
 * Custom hook for handling automation step actions (copy, delete, move up/down).
 *
 * This hook provides operations to manipulate automation template steps, including:
 * - Copying/duplicating steps with schedule adjustment
 * - Deleting steps from the template
 * - Moving steps up or down in the sequence
 *
 * @param params - Configuration object containing template ID, steps array, trigger, and callbacks
 * @returns Object containing action functions and loading state
 */
export function useStepActions(params: UseStepActionsParams): UseStepActionsResult {
	const {
		automationTemplateId,
		steps,
		automationTemplateTrigger,
		impersonationContext,
		onStepsChanged,
		createEmptyStep,
	} = params;

	const duplicateStepMutation = useAutomationTemplateDuplicateStepMutation();
	const removeStepMutation = useAutomationTemplateRemoveStepMutation();
	const updateStepMutation = useUpdateAutomationStepMutation();

	const isBusy = duplicateStepMutation.isPending || removeStepMutation.isPending || updateStepMutation.isPending;

	/**
	 * Copies a step by duplicating it in the automation template.
	 * Adjusts the schedule criteria if the new position differs from the original.
	 *
	 * After duplication, the caller should reload the steps from the template
	 * to get properly constructed ViewModels.
	 *
	 * @param step - The step to copy
	 * @param _beforeStepId - Optional ID of the step before which to insert the copy (reserved for future use)
	 */
	const copyStep = useCallback(
		async (step: AutomationEditorStep, _beforeStepId?: string) => {
			if (!step.automationStep?.id) {
				// Copy empty step (no API call needed)
				if (createEmptyStep) {
					await createEmptyStep();
				}
				return;
			}

			const stepIndex = steps.indexOf(step);
			if (stepIndex === -1) {
				return;
			}

			// Calculate if schedule criteria should be adjusted based on new position
			// Note: we check if the default schedule criteria for the duplicate would differ given the new index,
			// and we go with that value over the original value.
			// This prevents creating a step with index > 0 with schedule criteria === Api.AutomationStepScheduleCriteria.Immediately
			// if duplicating, for example, an email step at index === 0
			const defaultSchedule = Api.VmUtils.Automations.steps.getDefaultScheduleForAutomationStep(
				step.automationStep._type as Api.AutomationStepType,
				automationTemplateTrigger
			);

			const shouldUpdateSchedule = defaultSchedule.criteria !== step.automationStep.schedule?.criteria;

			// Call the duplicate API
			const duplicatedStepData = await duplicateStepMutation.mutateAsync({
				templateId: automationTemplateId,
				stepId: step.automationStep.id,
				impersonationContext,
			});

			// Update schedule if necessary
			if (shouldUpdateSchedule && duplicatedStepData.id) {
				const updatedSchedule: Api.IAutomationStepSchedule = {
					...(step.automationStep?.schedule || {}),
					criteria: defaultSchedule.criteria,
				};

				// Determine step type from the duplicated data
				const stepType = Api.VmUtils.Automations.steps.getAutomationStepTypeForAutomationStep(duplicatedStepData);

				if (stepType) {
					await updateStepMutation.mutateAsync({
						templateId: automationTemplateId,
						stepType,
						stepId: duplicatedStepData.id,
						step: {
							...duplicatedStepData,
							schedule: updatedSchedule,
						},
						impersonationContext,
					});
				}
			}
			// Note: No manual reload needed - mutations invalidate the query cache
			// and useAutomationEditorSteps auto-syncs with the updated data
		},
		[
			automationTemplateId,
			automationTemplateTrigger,
			duplicateStepMutation,
			impersonationContext,
			steps,
			updateStepMutation,
			createEmptyStep,
		]
	);

	/**
	 * Deletes a step from the automation template.
	 *
	 * @param step - The step to delete
	 */
	const deleteStep = useCallback(
		async (step: AutomationEditorStep) => {
			const stepIndex = steps.indexOf(step);
			if (stepIndex === -1) {
				return;
			}

			if (!step.automationStep?.id) {
				// Remove empty step (no API call needed)
				const updatedSteps = [...steps];
				updatedSteps.splice(stepIndex, 1);
				onStepsChanged(updatedSteps);
				return;
			}

			// Determine step type from the automation step
			const stepType = Api.VmUtils.Automations.steps.getAutomationStepTypeForAutomationStep(step.automationStep);
			if (!stepType) {
				return;
			}

			// Call the remove API
			await removeStepMutation.mutateAsync({
				templateId: automationTemplateId,
				stepType,
				stepId: step.automationStep.id,
				impersonationContext,
			});

			// Remove the step from the array
			const nextSteps = [...steps];
			nextSteps.splice(stepIndex, 1);
			onStepsChanged(nextSteps);
		},
		[automationTemplateId, impersonationContext, onStepsChanged, removeStepMutation, steps]
	);

	/**
	 * Moves a step up in the sequence (decreases index).
	 * This is a local-only operation that updates the array order.
	 *
	 * @param step - The step to move up
	 */
	const moveStepUp = useCallback(
		(step: AutomationEditorStep) => {
			const stepIndex = steps.indexOf(step);
			if (stepIndex <= 0) {
				return;
			}

			// Swap with the previous step
			const nextSteps = [...steps];
			const temp = nextSteps[stepIndex - 1];
			nextSteps[stepIndex - 1] = nextSteps[stepIndex];
			nextSteps[stepIndex] = temp;

			// Update the local state
			onStepsChanged(nextSteps);

			// Note: Order is maintained locally. The backend persists order based on the
			// sequence of step IDs when the template is saved or published.
		},
		[onStepsChanged, steps]
	);

	/**
	 * Moves a step down in the sequence (increases index).
	 * This is a local-only operation that updates the array order.
	 *
	 * @param step - The step to move down
	 */
	const moveStepDown = useCallback(
		(step: AutomationEditorStep) => {
			const stepIndex = steps.indexOf(step);
			if (stepIndex === -1 || stepIndex >= steps.length - 1) {
				return;
			}

			// Swap with the next step
			const nextSteps = [...steps];
			const temp = nextSteps[stepIndex + 1];
			nextSteps[stepIndex + 1] = nextSteps[stepIndex];
			nextSteps[stepIndex] = temp;

			// Update the local state
			onStepsChanged(nextSteps);

			// Note: Order is maintained locally. The backend persists order based on the
			// sequence of step IDs when the template is saved or published.
		},
		[onStepsChanged, steps]
	);

	return {
		copyStep,
		deleteStep,
		moveStepUp,
		moveStepDown,
		isBusy,
	};
}
